// @flow
import {useMemo} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {Models} from '@wellstone-solutions/common';
import type {ApiResponseType} from '@wellstone-solutions/common';
import {
  documentationSessionDocumentsCreate,
  documentationSessionDocumentsUpdate,
  documentationSessionDocumentsPartialUpdate,
  documentationSessionDocumentsRetrieve,
  documentationSessionDocumentsDestroy,
  documentationSessionDocumentsPdfRetrieve,
} from 'api/v2';
import {useStores} from 'hooks/useStores';
import {useFeatureFlag, LD_FLAGS} from 'modules/feature-flags';
import type {
  UISessionDocumentType,
  UISessionServiceType,
  SessionDocumentStatus,
  ApiPDFResponseType,
} from '../../model/types';
import {sessionDocumentKeys} from '../../queries/keys';
import {orgServiceTypesQueryOptions, useOrgServiceTypes} from '../../queries';
import {resolveDocuments} from '../../queries/resolveDocuments';
import {toApiRequest, toUIServiceTypes} from '../../queries/adapter';

const {Integration} = Models;

type ActionsType = {
  serviceTypes: Array<UISessionServiceType>,
  getServiceTypes: () => Promise<ApiResponseType<Array<UISessionServiceType>>>,
  getSessionDoc: (
    id: string,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
  createSessionDoc: (
    sessionDoc: UISessionDocumentType,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
  updateSessionDoc: (
    id: string,
    sessionDoc: UISessionDocumentType,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
  updateSessionDocStatus: (
    id: string,
    sessionDoc: UISessionDocumentType,
    status: SessionDocumentStatus,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
  removeSessionDoc: (id: string) => Promise<ApiResponseType<{}>>,
  downloadPDF: (id: string) => Promise<ApiResponseType<ApiPDFResponseType>>,
  createExternalEHRDocument: (
    id: string,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
  updateExternalEHRDocument: (
    id: string,
  ) => Promise<ApiResponseType<UISessionDocumentType>>,
};

// Normalizes an api-client (axios) promise to the {isSuccess, data} contract the
// documentation UI expects — the V2 axios instance rejects on error and returns
// raw responses, unlike V1/REST which are pre-normalized by an interceptor.
const handle = async <T>(
  request: Promise<{data: T}>,
): Promise<ApiResponseType<T>> => {
  try {
    const {data} = await request;
    // $FlowIgnoreMe - building the normalized ApiResponseType shape
    return {isSuccess: true, data};
  } catch (error) {
    // $FlowIgnoreMe - building the normalized ApiResponseType shape
    return {
      isSuccess: false,
      errors: error?.response?.data ?? [error],
      status: error?.response?.status ?? error?.status,
    };
  }
};

const triggerPdfDownload = ({file, filename}: ApiPDFResponseType): void => {
  const url = window.URL.createObjectURL(
    new Blob([atob(file)], {type: 'application/pdf'}),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body?.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
};

// Single call site for documentation reads/writes. V2 goes through the api-client
// + React Query cache; V1 (behind the documentation_v2 flag) delegates to the
// legacy MobX store. Keeping the {isSuccess, data} shape here means the shared
// form/view/list components don't care which path is active.
//
// The method object is memoized on stable singletons so callers can safely list
// the returned functions in effect/useCallback deps.
export const useSessionDocumentActions = (): ActionsType => {
  const isV2 = useFeatureFlag(LD_FLAGS.DOCUMENTATION_V2);
  const {sessionDocumentStore, meStore, memberStore} = useStores();
  const queryClient = useQueryClient();
  const orgServiceTypesQuery = useOrgServiceTypes({enabled: isV2});

  const methods = useMemo<Object>(() => {
    if (!isV2) {
      return {
        getServiceTypes: () => sessionDocumentStore.getServiceTypes(),
        getSessionDoc: (id) => sessionDocumentStore.getSessionDoc(id),
        createSessionDoc: (doc) => sessionDocumentStore.createSessionDoc(doc),
        updateSessionDoc: (id, doc) =>
          sessionDocumentStore.updateSessionDoc(id, doc),
        updateSessionDocStatus: (id, doc, status) =>
          sessionDocumentStore.updateSessionDocStatus(id, doc, status),
        removeSessionDoc: (id) => sessionDocumentStore.removeSessionDoc(id),
        downloadPDF: (id) => sessionDocumentStore.downloadPDF(id),
        createExternalEHRDocument: (id) =>
          sessionDocumentStore.createExternalEHRDocument(id),
        updateExternalEHRDocument: (id) =>
          sessionDocumentStore.updateExternalEHRDocument(id),
      };
    }

    const invalidate = () =>
      queryClient.invalidateQueries({queryKey: sessionDocumentKeys.all});

    // Resolve a single raw V2 doc (member/staff/service names) into the UI shape.
    const resolveOne = async (apiDoc) => {
      const [uiDoc] = await resolveDocuments([apiDoc], {
        memberStore,
        queryClient,
      });
      return uiDoc;
    };

    const getSessionDoc = async (id) => {
      const response = await handle(documentationSessionDocumentsRetrieve(id));
      if (!response.isSuccess) {
        return response;
      }
      return {...response, data: await resolveOne(response.data)};
    };

    const writeAndResolve = async (request) => {
      const response = await handle(request);
      if (!response.isSuccess) {
        return response;
      }
      invalidate();
      return {...response, data: await resolveOne(response.data)};
    };

    return {
      getServiceTypes: async () => {
        const response = await handle(
          queryClient.fetchQuery(orgServiceTypesQueryOptions()),
        );
        if (!response.isSuccess) {
          return response;
        }
        return {...response, data: toUIServiceTypes(response.data.results)};
      },

      getSessionDoc,

      createSessionDoc: (sessionDoc) =>
        writeAndResolve(
          documentationSessionDocumentsCreate(toApiRequest(sessionDoc)),
        ),

      updateSessionDoc: (id, sessionDoc) =>
        writeAndResolve(
          documentationSessionDocumentsUpdate(id, toApiRequest(sessionDoc)),
        ),

      updateSessionDocStatus: (id, sessionDoc, status) =>
        writeAndResolve(
          documentationSessionDocumentsPartialUpdate(
            id,
            toApiRequest({...sessionDoc, status}),
          ),
        ),

      removeSessionDoc: async (id) => {
        const response = await handle(documentationSessionDocumentsDestroy(id));
        if (response.isSuccess) {
          invalidate();
        }
        return response;
      },

      downloadPDF: async (id) => {
        const response = await handle(
          documentationSessionDocumentsPdfRetrieve(id),
        );
        if (response.isSuccess) {
          triggerPdfDownload(response.data);
        }
        return response;
      },

      // EHR create/update remain V1 endpoints; on success re-fetch through V2 so
      // the returned document matches the current response contract (and
      // resolves member/staff names).
      createExternalEHRDocument: async (id) => {
        const response = await Integration.create(meStore.organizationId, id);
        if (!response.isSuccess) {
          return response;
        }
        return getSessionDoc(id);
      },

      updateExternalEHRDocument: async (id) => {
        const response = await Integration.update(meStore.organizationId, id);
        if (!response.isSuccess) {
          return response;
        }
        return getSessionDoc(id);
      },
    };
  }, [isV2, sessionDocumentStore, meStore, memberStore, queryClient]);

  const serviceTypes = isV2
    ? (orgServiceTypesQuery.data ?? [])
    : Array.from(sessionDocumentStore.serviceTypes);

  return {...methods, serviceTypes};
};
